import styled, { useTheme } from 'styled-components';
import { PSmall } from './Text';
import DropdownMenu from './DropdownMenu';
import { DropdownItemRow } from '../Admin/PillFilter';
import Checkbox from './Checkbox';

export const FiltersHorizontalWrapper = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  align-items: center;
  flex-wrap: wrap;
`;

const AddIcon = styled.i.attrs({ className: 'fa-solid fa-plus' })`
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 12px;
`;
const XIcon = styled.i.attrs({ className: 'fa-solid fa-xmark' })`
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 12px;
  cursor: pointer;
`;
const ChevronIcon = styled.i.attrs({ className: 'fa-solid fa-chevron-down' })`
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 12px;
  margin-left: 8px;
`;
const PillDivider = styled.span`
  display: inline-block;
  width: 2px;
  height: 16px;
  background: ${({ theme }) => theme.colors.bgActive};
  margin: 0 8px;
  vertical-align: middle;
`;

const FilterInput = styled.input`
  background: none !important;
  border: none !important;
  outline: none;
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 14px;
  width: 250px;
  cursor: pointer;
  padding: 0 12px;
  margin: 0;
  height: 42px;
  border-radius: 0;
`;

export const InactiveFilterPillWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${({ theme }) => theme.colors.transparentBg};
  border-radius: 999px;
  padding: 4px 16px;
  border: 1px dashed ${({ theme }) => theme.colors.bgActive};
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: ${({ theme }) => theme.colors.bgActive};
  }
`;

export const ActiveFilterPillWrapper = styled(InactiveFilterPillWrapper)`
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  background-color: ${({ theme }) => theme.colors.bgActive};
`;

export const FilterLabel = styled(PSmall)`
  margin: 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.textRegular};
  font-weight: regular;
  white-space: nowrap;
`;

export const PillFilter = ({
  label,
  values,
  placeholder,
  onClear,
  onClick,
  hideClear,
  inactive,
  children,
}: {
  label: string;
  values: string[];
  placeholder?: string;
  onClear?: (e: React.MouseEvent) => void;
  onClick?: () => void;
  hideClear?: boolean;
  inactive?: boolean;
  children: React.ReactNode;
}) => {
  let displayText = '';
  if (values.length > 2) {
    displayText = `${values.slice(0, 2).join(', ')} …`;
  } else if (values.length > 0) {
    displayText = values.join(', ');
  } else if (placeholder) {
    displayText = placeholder;
  }

  const theme = useTheme();

  const pill = inactive ? (
    <InactiveFilterPillWrapper onClick={onClick}>
      <AddIcon />
      <FilterLabel>{label}</FilterLabel>
    </InactiveFilterPillWrapper>
  ) : (
    <ActiveFilterPillWrapper onClick={onClick}>
      {!hideClear && (
        <XIcon
          onClick={(e) => {
            e.stopPropagation();
            onClear && onClear(e);
          }}
        />
      )}
      <FilterLabel>
        {label}
        {displayText && (
          <>
            <PillDivider />
            <span style={{ color: theme.colors.textRegular }}>
              {displayText}
            </span>
          </>
        )}
      </FilterLabel>
      <ChevronIcon />
    </ActiveFilterPillWrapper>
  );

  return <DropdownMenu trigger={pill}>{children}</DropdownMenu>;
};

const FilterDropdownContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`;

const FilterDropdownContentWrapper = styled.div`
  display: flex;
  align-items: center;
  border-radius: 8px;
  height: 42px;
  position: relative;
  background: none;
  justify-content: space-between;
  width: 100%;
`;

export const TextFilterPill = ({
  label,
  value,
  placeholder = 'Type a value...',
  onChange,
}: {
  placeholder?: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) => {
  return (
    <PillFilter
      label={label}
      values={value ? [value] : []}
      onClear={(e) => {
        e.stopPropagation();
        onChange('');
      }}
      inactive={!value}
    >
      <FilterDropdownContent>
        <FilterDropdownContentWrapper>
          <FilterInput
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{ width: '100%' }}
          />
        </FilterDropdownContentWrapper>
      </FilterDropdownContent>
    </PillFilter>
  );
};

export const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
  margin-bottom: 0;
`;
export const CheckboxIconWrapper = styled.span`
  display: flex;
  align-items: center;
`;

export const DropdownItemLabel = styled(PSmall)`
  margin: 0;
  margin-left: 8px;
  padding: 0;
  color: ${({ theme }) => theme.colors.textRegular};
  font-weight: regular;
  white-space: nowrap;
`;

export const SingleSelectFilterPill = ({
  label,
  value,
  options,
  onChange,
  shouldHideClear = false,
}: {
  label: string;
  placeholder?: string;
  value: string;
  options: { label: string; value: string }[];
  onChange: (value: string) => void;
  shouldHideClear?: boolean;
}) => {
  return (
    <PillFilter
      label={label}
      values={[value]}
      hideClear={shouldHideClear}
      inactive={!value}
    >
      {options.map((option) => (
        <DropdownItemRow
          key={option.value}
          tabIndex={0}
          role="button"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          <CheckboxLabel>
            <CheckboxIconWrapper>
              <Checkbox
                isChecked={value === option.value}
                onChange={() => onChange(option.value)}
              />
            </CheckboxIconWrapper>
            <DropdownItemLabel>{option.label}</DropdownItemLabel>
          </CheckboxLabel>
        </DropdownItemRow>
      ))}
    </PillFilter>
  );
};

export const MultiSelectFilterPill = ({
  label,
  values,
  options,
  onChange,
}: {
  label: string;
  values: string[];
  options: { label: string; value: string }[];
  onChange: (values: string[]) => void;
}) => {
  return (
    <PillFilter
      label={label}
      values={values}
      onClear={(e) => {
        e.stopPropagation();
        onChange([]);
      }}
      inactive={values.length === 0}
    >
      {options.map((option) => {
        const isChecked = values.includes(option.value);
        return (
          <DropdownItemRow
            key={option.value}
            tabIndex={0}
            role="button"
            aria-pressed={isChecked}
            onKeyDown={(e) => {
              if (e.key === ' ' || e.key === 'Enter') {
                onChange(
                  isChecked
                    ? values.filter((s) => s !== option.value)
                    : [...values, option.value]
                );
              }
            }}
          >
            <CheckboxLabel>
              <CheckboxIconWrapper onMouseDown={(e) => e.stopPropagation()}>
                <Checkbox
                  isChecked={isChecked}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    onChange(
                      e.target.checked
                        ? [...values, option.value]
                        : values.filter((s) => s !== option.value)
                    );
                  }}
                />
              </CheckboxIconWrapper>
              <DropdownItemLabel>{option.label}</DropdownItemLabel>
            </CheckboxLabel>
          </DropdownItemRow>
        );
      })}
    </PillFilter>
  );
};

const FilterPaddedContent = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  padding: 4px 14px;
`;

export const TextOrEmptyFilterPill = ({
  label,
  nullLabel = 'No value',
  value,
  placeholder = 'Type a value...',
  onChange,
}: {
  label: string;
  nullLabel?: string;
  value: string | null;
  placeholder?: string;
  onChange: (value: string | null) => void;
}) => {
  return (
    <PillFilter
      label={label}
      values={(() => {
        const vals = [];
        if (value === null) vals.push(nullLabel);
        if (value) vals.push(value);
        return vals;
      })()}
      onClear={(e) => {
        e.stopPropagation();
        onChange('');
      }}
      inactive={value === ''}
    >
      <FilterDropdownContent
        style={{ flexDirection: 'column', alignItems: 'stretch' }}
      >
        <CheckboxLabel>
          <FilterPaddedContent>
            <CheckboxIconWrapper>
              <Checkbox
                isChecked={value === null}
                onChange={(e) => onChange(e.target.checked ? null : '')}
              />
            </CheckboxIconWrapper>
            <DropdownItemLabel style={{ marginLeft: 0 }}>
              {nullLabel}
            </DropdownItemLabel>
          </FilterPaddedContent>
        </CheckboxLabel>
        <FilterDropdownContentWrapper>
          <FilterInput
            type="text"
            placeholder={placeholder}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            style={{ width: '100%' }}
          />
        </FilterDropdownContentWrapper>
      </FilterDropdownContent>
    </PillFilter>
  );
};

const DateDropdownContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`;
const CombinedDatePickerWrapper = styled.div`
  display: flex;
  align-items: center;
  border-radius: 8px;
  height: 42px;
  position: relative;
  background: none;
  justify-content: space-between;
  width: 100%;
`;
const DatePickerInput = styled.input`
  background: none !important;
  border: none !important;
  outline: none;
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 14px;
  width: 140px;
  cursor: pointer;
  padding: 0 12px;
  margin: 0;
  height: 42px;
  border-radius: 0;
`;
const DateDropdownLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textRegular};
`;

export const DateRangeFilterPill = ({
  label,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
}: {
  label: string;
  startDate: string;
  endDate: string;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
}) => {
  // Format date for display (DD/MM/YYYY)
  const formatDate = (date: string) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-GB');
  };
  let datePillValue = '';
  if (startDate || endDate) {
    datePillValue = `${startDate ? formatDate(startDate) : '...'} - ${
      endDate ? formatDate(endDate) : '...'
    }`;
  }

  return (
    <PillFilter
      label={label}
      values={datePillValue ? [datePillValue] : []}
      onClear={(e) => {
        e.stopPropagation();
        setStartDate('');
        setEndDate('');
      }}
      inactive={!startDate && !endDate}
    >
      <DateDropdownContent>
        <CombinedDatePickerWrapper>
          <DatePickerInput
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            onClick={(e) => e.currentTarget.showPicker()}
          />
          <DateDropdownLabel>to</DateDropdownLabel>
          <DatePickerInput
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            onClick={(e) => e.currentTarget.showPicker()}
          />
        </CombinedDatePickerWrapper>
      </DateDropdownContent>
    </PillFilter>
  );
};
